import { match } from '@/common/utils/match';
import type { Context, FeatureToggles } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  CalculationState,
  LoadFlowScenarioResults,
  CalculationScenarios,
  CompletedCalculationTactic,
  VoltageLevel,
  ShortCircuitScenarioResults
} from '../..';
import { isCalculationTacticResultUnavailable } from '../../actions';
import type { EvaluationRule } from '../../evaluation/EvaluationRule';
import type { GridElementsDetailsByCalculationType } from './GridElementsDetails';
import type { AttentionStatuses } from './evaluateByCalculationType';
import { evaluateByCalculationType } from './evaluateByCalculationType';
import { Features } from '../../createGridElementCalculationSetExecutionDetails';

export const createDetailsFilteredBySelectedScenarios = (
  state: CalculationState,
  rootState: Context['state']
): CalculationState['gridElementsDetailsByCalculationType'] => {
  if(state.evaluationRules.type === 'UNAVAILABLE') {
    return { type: 'NO_EVALUATION' };
  }
  if (
    state.calculationTactic.current !== 'LOADED' ||
    state.calculationTactic.value.status !== 'Completed' ||
    isCalculationTacticResultUnavailable(state.calculationTactic.value)
  )
    return { type: 'NO_EVALUATION' };

  const calculationTactic = state.calculationTactic.value;
  const voltageLevel = calculationTactic?.calculationSetResult.voltageLevel;

  const features =
    rootState.arcgis.featureGeometries.current === 'LOADED'
      ? rootState.arcgis.featureGeometries.value.flatMap(
          layer => layer.features
        )
      : [];

  const _features = new Features(features as FeatureWithIDs[]);

  const createDetails = createDetailsWithLoadFlowFilter(
    _features,
    state.calculationTactic.value.calculationSetResult.calculationScenarios,
    state.evaluationRules.rules,
    state.calculationTactic.value,
    voltageLevel,
    rootState.featureToggles
  );
  // TODO: Don't filter it here. Create results for each and filter in separate derived state
  const gridElements: GridElementsDetailsByCalculationType & AttentionStatuses =
    match.allOf(state.tacticResultBrowsingFilter.type, {
      ALL: () => createDetails(),
      ALL_LOAD_FLOW: () => ({
        ...createDetails(),
        shortCircuit: []
      }),
      SELECTED_LOAD_FLOW: () => ({
        ...createDetails(loadFlowResult =>
          selectedScenariosFilter(loadFlowResult, state)
        ),
        shortCircuit: []
      }),
      SHORT_CIRCUIT: () => ({ ...createDetails(), loadFlow: [] }),
      NONE_AVAILABLE: () => ({
        shortCircuit: [],
        loadFlow: [],
        gridElementPropertyAttentionStatus: { loadFlow: [], shortCircuit: [] }
      })
    });

  const gridElementsDetails = {
    shortCircuit: gridElements.shortCircuit,
    loadFlow: gridElements.loadFlow
  };
  const attentionStatuses = gridElements.gridElementPropertyAttentionStatus;

  return {
    type: 'EVALUATED',
    gridElementsDetails,
    attentionStatuses // TODO: Move evaluation of each element (not filtered) to separate state
  };
};

function noFilter() {
  return true;
}

function selectedScenariosFilter<
  T extends LoadFlowScenarioResults | ShortCircuitScenarioResults
>(result: T, state: CalculationState) {
  return (
    state.tacticResultBrowsingFilter as {
      type: 'SELECTED_LOAD_FLOW';
      scenarioIds: string[];
    }
  ).scenarioIds.some(scenarioId => scenarioId === result.scenarioId);
}

export const createDetailsWithLoadFlowFilter = (
  features: Features,
  calculationScenarios: CalculationScenarios,
  evaluationRules: EvaluationRule[],
  calculationTactic: CompletedCalculationTactic,
  voltageLevel: VoltageLevel,
  featureToggles?: FeatureToggles
) => {
  return (
    loadFlowFilterFunction: (
      results: LoadFlowScenarioResults
    ) => boolean = noFilter
  ) => {
    return evaluateByCalculationType(
      features,
      calculationScenarios,
      evaluationRules,
      calculationTactic,
      loadFlowFilterFunction,
      voltageLevel,
      featureToggles
    );
  };
};
